import { Box, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import Datagrid from "../Datagrid/Datagrid";
import "./AnalyticsCharts.css";
import { useSite } from "../../context/siteContext";
import GradientLineChart from "../Carts/GradientLineChart/GradientLineChart.js/GradientLineChart";
import LineChart from "../Carts/LineChart/LineChart";
import dayjs from "dayjs"; // Import dayjs
import DonutChart from "../Carts/DonutChart/DonutChart";
import StackedHorizontalBarChart from "../Carts/StackedHorizontalBarChart/StackedHorizontalBarChart";
import BarChart from "../Carts/BarChart/BarChart";
import GeoMapChart from "../Carts/GeoMapChart/GeoMapChart";
import TreeMap from "../Carts/TreeMap/TreeMap";
import SunBurst from "../Carts/SunBurst/SunBurst";
import SeriesBarChart from "../Carts/SeriesBarChart/SeriesBarChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import CrossCharts from "../Carts/CrossChart/CrossChart";
import HorizontalBarChart from "../Carts/HorizontalBarChart/HorizontalBarChart";
import ChartLoading from "../Carts/ChartLoading/ChartLoading";
import TableChartIcon from "@mui/icons-material/TableChart";
import NoDataText from "../Carts/NoDataText/NoDataText";
import StackedGradientLineChart from "../Carts/StackedGradientLineChart/StackedGradientLineChart";
import CloseIcon from "@mui/icons-material/Close";
import { MENU_ITEMS, METRICS, ORDER_STATUSES, UI_TEXT } from "../../constants";
import { TABLE_HEADERS } from "../../constants";
import { useTranslation } from "react-i18next";
import { getAnalyticsOrders, getMostRequestedParts } from "../../services/analyticsChartService";

// Utility function to format a date
const formatDate = (date, formatStr = "ddd M/D") => {
	return dayjs(date).format(formatStr); // Default is "Tue 3/18"
};

// Utility function to group by week
const groupByWeek = (date) => {
	const start = dayjs(date).startOf("week");
	const end = dayjs(date).endOf("week");
	return `Week ${start.format("M/D")} - ${end.format("M/D")}`;
};

// Utility function to group by month
const groupByMonth = (date) => {
	return dayjs(date).format("MMM YYYY"); // "Mar 2025"
};

// Utility function to group by quarter
const groupByQuarter = (date) => {
	const quarterStart = dayjs(date).startOf("quarter");
	const quarterEnd = dayjs(date).endOf("quarter");
	return `Q${Math.floor((quarterStart.month() + 3) / 3)} ${quarterStart.format("M/D")} - ${quarterEnd.format("M/D")}`;
};

// Utility function to group by year
const groupByYear = (date) => {
	return dayjs(date).format("YYYY"); // "2025"
};

const formatISODate = (isoDate) => {
	const date = new Date(isoDate);
	const year = date?.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0"); // month is 0-based
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const columns = [
	{ field: "product_name", headerName: 'TABLE_HEADERS.PRODUCT_NAME', flex: 1 },
	{ field: "product_number", headerName:'TABLE_HEADERS.PRODUCT_NUMBER', flex: 1 },
	{ field: "count", headerName:'TABLE_HEADERS.COUNT', flex: 1 },
];


const AnalyticsCharts = ({
	loading,
	chartFilters = {},
	setLoading,
	selectedRange,
	additionalFilters,
	setAdditionalFilters,
	setShowCharts,
	setSelectedMetric,
	selectedMetric,
}) => {
	const [chartData, setChartData] = useState([]);
	const [requestedParts, setRequestedParts] = useState([]);
	const { currencySymbol } = useSite();
	const [orders, setOrders] = useState([]);
	const [metrics, setMetrics] = useState([]);
	const [metricsLoading, setMetricsLoading] = useState(true);
	const [requestedPartsLoading, setRequestedPartsLoading] = useState(true);
	const [hasOrders, setHasOrders] = useState(true);
	const chartKeys = [
		"orderStatuses",
		"topParts",
		"topCustomers",
		"topSites",
		"ordersByCustomer",
		"ordersBySite",
		"ordersByCities",
		"ordersRevenue",
		"ordersByDate",
		"ordersRevenueCount",
		"treeMapData",
		"customerOrderPatterns",
		"ordersByPaymentModes",
		"ordersRevenueByPaymentMode",
	];
	const getChartLoadingState = (isLoading) =>
		chartKeys.reduce((acc, key) => {
			acc[key] = isLoading;
			return acc;
		}, {});
	const [chartLoading, setChartLoading] = useState(
		chartKeys.reduce((acc, key) => {
			acc[key] = true;
			return acc;
		}, {})
	);
	const lastQueryRef = useRef("");
	const requestIdRef = useRef(0);
	const {t} = useTranslation();

	useEffect(() => {
		const queryKey = JSON.stringify(chartFilters || {});
		if (queryKey === lastQueryRef.current) return;
		lastQueryRef.current = queryKey;
		if (Object.keys(chartFilters).length === 0) return;

		const requestId = requestIdRef.current + 1;
		requestIdRef.current = requestId;
		const isCurrentRequest = () => requestIdRef.current === requestId;

		setLoading(true);
		setMetricsLoading(true);
		setRequestedPartsLoading(true);
		setChartLoading(getChartLoadingState(true));

		const fetchOrders = async (req_body) => {
			try {
				const response = await getAnalyticsOrders({
					...req_body,
				});
				if (!isCurrentRequest()) return;
				console.log("In Analytics charts getanalyticsorders response", response);
				await aggregateOrderData(response);
			} catch (error) {
				console.log("Error in fetchOrders", error);
				if (isCurrentRequest()) {
					setHasOrders(false);
					setOrders([]);
					setChartData({});
				}
			}
		};

		const fetchRequestedParts = async (chartFilters) => {
			try {
				const response = await getMostRequestedParts({
					...chartFilters,
				});
				if (!isCurrentRequest()) return;
				console.log("In Analytics charts getmostrequestedparts response", response);
				if (response?.status) {
					const topRequested = generateTopRequestedParts(response.data);
					console.log(topRequested);
					// Assuming you want to setChartData by merging with existing data
					setRequestedParts(topRequested);
				} else {
					console.log("No data found for requested parts");
					setRequestedParts([]);
				}
			} catch (error) {
				console.log("Error in fetchReqList: ", error);
				if (isCurrentRequest()) {
					setRequestedParts([]);
				}
			}
		};

		Promise.allSettled([
			fetchOrders(chartFilters),
			fetchRequestedParts(chartFilters),
		]).finally(() => {
			if (!isCurrentRequest()) return;
			setMetricsLoading(false);
			setRequestedPartsLoading(false);
			setChartLoading(getChartLoadingState(false));
			setLoading(false);
		});
	}, [chartFilters]);

	const aggregateOrderData = async (order_data) => {
		if (order_data.count !== 0) {
			const order_metrics = order_data.order_metrics;
			const trends = order_data.trends;
			console.log("Inside aggregate order data response of order data",order_data);
			await updateMetrics(order_metrics, trends);
			await generateChartsData(order_data);
		} else {
			await updateMetrics();
			await generateChartsData(order_data);
		}
	};

	const capitalizeFirstLetter = (str) => {
		return str.charAt(0).toUpperCase() + str.slice(1);
	};

	const updateMetrics = async (order_metrics = {}, trends = {}) => {
		const hasData = Object.keys(order_metrics).length > 0 && Object.keys(trends).length > 0;

		const formatCurrency = (value) => {
			const absValue = Math.abs(value);
			let formattedValue;
			let suffix = "";

			if (absValue >= 1e9) {
				formattedValue = value / 1e9;
				suffix = "B";
			} else if (absValue >= 1e6) {
				formattedValue = value / 1e6;
				suffix = "M";
			} else if (absValue >= 1e3) {
				formattedValue = value / 1e3;
				suffix = "K";
			} else {
				formattedValue = value;
			}

			return `${currencySymbol}${formattedValue.toFixed(2)}${suffix}`;
		};

		const createMetric = (title, value, change, changeType, key) => ({
			title,
			value,
			change: `${change}%`,
			changeType,
			key,
		});

		const defaultMetrics = [
			// createMetric(t('METRICS.TOTAL_EARNINGS'), formatCurrency(0), "0", "warning", "total_revenue"),
			createMetric(t('METRICS.TOTAL_ORDERS'), "0", "0", "warning", "total_orders"),
			// createMetric("Shipped Orders", "0", "0", "warning"),
			createMetric(t('METRICS.PENDING_ORDERS'), "0", "0", "warning", "Pending"),
			createMetric(t('METRICS.INVOICE_ORDERS'), "0", "0", "warning", "Invoiced"),
			createMetric(t('METRICS.DELIVERED_ORDERS'), "0", "0", "warning", "Delivered"),
			// createMetric(t('METRICS.AVERAGE_ORDER_VALUE'), formatCurrency(0), "0", "warning", "AOV"),
		];

		if (!hasData) {
			setMetrics(defaultMetrics);
			return;
		}

		const statusMetrics = ["pending", "invoiced", "delivered"].map((status) =>
			createMetric(
				`${capitalizeFirstLetter(status)} Quotes`,
				`${order_metrics.total_orders_by_status[status]}`,
				`${trends.total_orders_by_status[status].change}`,
				trends.total_orders_by_status[status].trend,
				capitalizeFirstLetter(status)
			)
		);

		setMetrics([
			createMetric(
				"Total Quotes",
				`${order_metrics.total_orders}`,
				`${trends.total_orders.change}`,
				trends.total_orders.trend,
				"total_orders"
			),
			...statusMetrics,
		]);
	};

	const generateChartsData = async (order_data = {}) => {
		const orderCount = Number.isFinite(Number(order_data?.count)) ? Number(order_data.count) : 0;
		const ordersData = Array.isArray(order_data?.data) ? order_data.data : [];
		const orderStatuses = order_data?.order_metrics?.total_orders_by_status || {};

		if (orderCount !== 0) {
			setHasOrders(true);
			setOrders(ordersData);
			const updateChart = async (key, task, mapValue) => {
				try {
					const value = typeof task === "function" ? await task() : await task;
					const mappedValue = typeof mapValue === "function" ? mapValue(value) : value;
					setChartData((prev) => ({ ...prev, [key]: mappedValue }));
				} catch (error) {
					console.log(`Chart data failed for ${key}`, error);
					setChartData((prev) => ({ ...prev, [key]: [] }));
				}
			};

			const tasks = [
				updateChart("orderStatuses", () => generateOrderStatusData(orderStatuses)),
				updateChart("topParts", () => generateTopOrderedParts(ordersData)),
				updateChart("ordersByCustomer", () => generateOrdersByCustomer(ordersData)),
				updateChart("ordersByCities", () => generateOrdersByCities(ordersData)),
				updateChart("ordersRevenue", () => generateRevenueData(ordersData)),
				updateChart("ordersByDate", () => generateOrdersbyDate(ordersData)),
				updateChart("ordersRevenueCount", () => generateOrdersRevenueCount(ordersData)),
				updateChart("treeMapData", () => generateTreeMapData(ordersData)),
				updateChart("ordersRevenueByPaymentMode", () => generateOrderRevenueByPaymentMode(ordersData)),
				updateChart("ordersByPaymentModes", () => generateOrdersByPaymentModes(ordersData), (value) => value.paymentModeArray),
				updateChart("topCustomers", () => generateTopCustomers(ordersData), (value) => value.customerDataset),
				updateChart("topSites", () => generateTopSites(ordersData), (value) => value.siteDataset),
				updateChart("ordersBySite", () => generateTopSites(ordersData), (value) => value.siteArray),
			];

			await Promise.allSettled(tasks);
		} else {
			setHasOrders(false);
			setOrders([]);
			setChartData({});
		}
	};

	const createDataset = (data) => {
		if (data.length === 0) return []; // Return empty if no data
		const keys = Object.keys(data[0]);
		return [
			{
				dimensions: keys,
				source: data.map((item) => keys.map((key) => item[key])),
			},
		];
	};

	const generateTreeMapData = async (orders) => {
		// Create the base object for transformation
		const result = {};

		orders.forEach((orderData) => {
			const { order } = orderData;
			const customerId = order.cus_id;
			const customerName = order.customer_name;
			const siteId = order.customer_site_id;
			const siteName = order.customer_site_name;
			const orderId = order.order_id;
			const orderNumber = order.order_number;
			const orderTotal = order.order_total || 0;

			// Initialize customer level if it doesn't exist
			if (!result[customerName]) {
				result[customerName] = {
					$count: 0,
				};
			}

			// Add to customer total
			result[customerName].$count += orderTotal;

			// Initialize site level if it doesn't exist
			if (!result[customerName][siteName]) {
				result[customerName][siteName] = {
					$count: 0,
				};
			}

			// Add to site total
			result[customerName][siteName].$count += orderTotal;

			// Initialize order level and set its value
			if (!result[customerName][siteName][orderNumber]) {
				result[customerName][siteName][orderNumber] = {
					$count: orderTotal,
				};
			}

			// If there are products, add them
			if (orderData.products && orderData.products.length > 0) {
				orderData.products.forEach((product) => {
					const productName =
						product.productsData.product_name || `Product-${product.productsData.product_name}`;
					const productTotal = product.product_total || product.product_price * product.quantity || 0;

					result[customerName][siteName][orderNumber][productName] = {
						$count: productTotal,
					};
				});
			} else {
				// If no products, add a placeholder to show the order amount
				result[customerName][siteName][orderNumber]["Order Total"] = {
					$count: orderTotal,
				};
			}
		});

		return result;

		// return {
		// 	Customer1: {
		// 	  $count: 360,
		// 	  Site1: {
		// 		$count: 180,
		// 		Order1: {
		// 		  $count: 120,
		// 		  Product1: { $count: 70 },
		// 		  Product2: { $count: 50 }
		// 		},
		// 		Order2: {
		// 		  $count: 60,
		// 		  Product3: { $count: 40 },
		// 		  Product4: { $count: 20 }
		// 		}
		// 	  },
		// 	  Site2: {
		// 		$count: 180,
		// 		Order3: {
		// 		  $count: 180,
		// 		  Product5: { $count: 100 },
		// 		  Product6: { $count: 80 }
		// 		}
		// 	  }
		// 	},
		// 	Customer2: {
		// 	  $count: 300,
		// 	  Site3: {
		// 		$count: 300,
		// 		Order4: {
		// 		  $count: 300,
		// 		  Product7: { $count: 150 },
		// 		  Product8: { $count: 150 }
		// 		}
		// 	  }
		// 	}
		//   };
	};


	const generateOrderStatusData = async (orderStatuses) => {
		const orderStatusData = [
			{ value: orderStatuses.pending, name: t('ORDER_STATUSES.PENDING') },
			{ value: orderStatuses.shipped, name: t('ORDER_STATUSES.SHIPPED') },
			{ value: orderStatuses.confirmed, name: t('ORDER_STATUSES.CONFIRMED') },
			{ value: orderStatuses.invoiced, name: t('ORDER_STATUSES.INVOICED') },
			{ value: orderStatuses.delivered, name:t('ORDER_STATUSES.DELIVERED') },
		];

		return orderStatusData;
	};

	const generateTopOrderedParts = async (orders) => {
		const partCounts = {};
		orders.forEach((order) => {
			order.products.forEach((item) => {
				const partId = item.product_id;
				if (partCounts[partId]) {
					partCounts[partId].count++;
					partCounts[partId].product_total += item.product_price;
				} else {
					partCounts[partId] = {
						count: 1,
						product_name: item.productsData.product_name,
						product_number: item.productsData.product_number,
						product_total: item.product_price,
					};
				}
			});
		});

		const partArray = Object.entries(partCounts).map(([partId, data]) => ({
			partId,
			count: data.count,
			product_name: data.product_name,
			product_number: data.product_number,
			product_total: data.product_total,
		}));

		const top_Parts = partArray.sort((a, b) => b.count - a.count).slice(0, 10);
		const dataset = createDataset(top_Parts);
		return dataset;
	};

	const generateTopRequestedParts = (productStats) => {
		const partArray = productStats.map((item) => ({
			partId: item.product_id,
			count: parseInt(item.occurrence_count, 10),
			product_name: item.product_name,
			product_number: item.product_number,
		}));

		// Sort by count in descending order
		const topParts = partArray.sort((a, b) => b.count - a.count).slice(0, 10);

		// Assuming createDataset formats the result for a chart or table
		const dataset = createDataset(topParts);
		return dataset;
	};

	const generateOrdersByCustomer = async (orders) => {
		const customerOrderCounts = {};
		const customerTotalRevenue = {};
		const customerSites = {};

		orders.forEach((order) => {
			const orderInfo = order?.order || {};
			const customerName =
				orderInfo.customer_name ||
				orderInfo.customer_number ||
				orderInfo.customer_site_name ||
				orderInfo.customer_site_number ||
				"N/A";
			const orderTotal = parseFloat(orderInfo.order_total) || 0;
			const siteNumber = orderInfo.customer_site_number || orderInfo.site_number || "";

			if (customerName) {
				// Increment order count for this site
				if (!customerOrderCounts[customerName]) {
					customerOrderCounts[customerName] = 1;
				} else {
					customerOrderCounts[customerName] += 1;
				}

				// Accumulate total revenue for this site
				if (!customerTotalRevenue[customerName]) {
					customerTotalRevenue[customerName] = orderTotal;
				} else {
					customerTotalRevenue[customerName] += orderTotal;
				}

				if (!customerSites[customerName]) {
					customerSites[customerName] = new Set();
				}
				if (siteNumber) {
					customerSites[customerName].add(String(siteNumber));
				}
			}
		});

		// Convert the customerOrderCounts and siteTotalRevenue objects to arrays for charting
		const customerNames = Object.keys(customerOrderCounts);
		const orderCounts = customerNames.map((customerName) => customerOrderCounts[customerName]);
		const totalRevenue = customerNames.map((customerName) => customerTotalRevenue[customerName]);
		const siteNumbers = customerNames.map((customerName) => {
			const sites = Array.from(customerSites[customerName] || []);
			return sites.length ? sites.join(", ") : "N/A";
		});

		// Create data for the chart
		const chartData = {
			customers: customerNames,
			counts: orderCounts,
			revenues: totalRevenue, // Include the total revenue for each site
			site_numbers: siteNumbers,
		};

		return chartData; // Set the chart data with both counts and revenues
	};

	const generateOrdersBySite = async (orders) => {
		const siteOrderCounts = {};
		const siteTotalRevenue = {};

		orders.forEach((order) => {
			const siteId = order.order.site_name;
			const orderTotal = order.order.order_total;

			if (siteId) {
				// Increment order count for this site
				if (!siteOrderCounts[siteId]) {
					siteOrderCounts[siteId] = 1;
				} else {
					siteOrderCounts[siteId] += 1;
				}

				// Accumulate total revenue for this site
				if (!siteTotalRevenue[siteId]) {
					siteTotalRevenue[siteId] = orderTotal;
				} else {
					siteTotalRevenue[siteId] += orderTotal;
				}
			}
		});

		// Convert the siteOrderCounts and siteTotalRevenue objects to arrays for charting
		const siteIds = Object.keys(siteOrderCounts);
		const orderCounts = siteIds.map((siteId) => siteOrderCounts[siteId]);
		const totalRevenue = siteIds.map((siteId) => siteTotalRevenue[siteId]);

		// Create data for the chart
		const chartData = {
			sites: siteIds,
			counts: orderCounts,
			revenues: totalRevenue, // Include the total revenue for each site
		};

		return chartData; // Set the chart data with both counts and revenues
	};

	const generateOrdersByCities = async (orders) => {
		const cityOrderCounts = orders.reduce((acc, order) => {
			const city = order.details.shipping_city;
			if (city) {
				if (!acc[city]) {
					acc[city] = 1; // First order for this city
				} else {
					acc[city] += 1; // Increment count for this city
				}
			}
			return acc;
		}, {});

		const cities = Object.keys(cityOrderCounts);
		const orderCounts = cities.map((city) => cityOrderCounts[city]);
		const geoMap = {
			cities: cities.map((city, index) => ({
				city,
				count: orderCounts[index],
			})),
		};

		return geoMap;
	};

	const generateRevenueData = async (orders = []) => {
		if (orders && orders.length > 0) {
			const revData = orders.map((order) => {
				return { date: formatISODate(order.order.order_created_date), value: order.order.order_total };
			});
			return revData;
		} else {
			return [];
		}
	};

	const generateOrdersbyDate = async (orders = []) => {
		if (orders && orders.length > 0) {
			const ordersByDate = {};
			orders.forEach((order) => {
				const date = formatISODate(order.order.order_created_date);
				if (!ordersByDate[date]) {
					ordersByDate[date] = 1;
				} else {
					ordersByDate[date] += 1;
				}
			});

			const orderCountData = Object.keys(ordersByDate).map((date) => {
				return { date: date, value: ordersByDate[date] };
			});

			orderCountData.sort((a, b) => new Date(a.date) - new Date(b.date));

			return orderCountData;
		} else {
			return [];
		}
	};

	const generateOrdersRevenueCount = (orders = []) => {
		if (orders && orders.length > 0) {
			const dataByDate = {};

			orders.forEach((order) => {
				const date = formatISODate(order.order.order_created_date);
				const orderTotal = parseFloat(order.order.order_total) || 0;
				if (!dataByDate[date]) {
					dataByDate[date] = { date, orders: 0, revenue: 0 };
				}
				dataByDate[date].orders += 1;
				dataByDate[date].revenue += orderTotal;
			});

			const formattedData = Object.values(dataByDate).map((entry) => ({
				date: entry.date,
				orders: entry.orders,
				revenue: Number(entry.revenue).toFixed(2),
			}));

			formattedData.sort((a, b) => new Date(b.date) - new Date(a.date));

			return formattedData;
		} else {
			return [];
		}
	};

	const generateTopCustomers = async (orders) => {
		console.log("Generate Top Customers incoming orders argument",orders);
		const customerCounts = {};
		orders.forEach((order) => {
			const customer_id = order.order.cus_id;
			if (customerCounts[customer_id]) {
				customerCounts[customer_id].count++;
				customerCounts[customer_id].customer_total += parseFloat(order.order.order_total);
			} else {
				customerCounts[customer_id] = {
					count: 1,
					customer_id,
					customer_name: order.order.customer_name,
					customer_number: order.order.customer_number,
					customer_total: parseFloat(order.order.order_total),
				};
			}
		});

		const customerArray = Object.entries(customerCounts).map(([customer_id, data]) => ({
			customer_id,
			count: data.count,
			customer_name: data.customer_name,
			customer_number: data.customer_number,
			customer_total: data.customer_total,
		}));

		const top_Customers = customerArray.sort((a, b) => b.count - a.count).slice(0, 10);
		const customerDataset = createDataset(top_Customers);
		return { customerDataset, customerArray };
	};

	const generateTopSites = async (orders) => {
		const siteCounts = {};
		orders.forEach((order) => {
			const site_id = order.order.customer_site_id;
			if (siteCounts[site_id]) {
				siteCounts[site_id].count++;
				siteCounts[site_id].site_total += parseFloat(order.order.order_total);
			} else {
				siteCounts[site_id] = {
					count: 1,
					site_id,
					site_name: order.order.customer_site_name,
					site_number: order.order.customer_site_number,
					site_total: parseFloat(order.order.order_total),
					customer_id: order.order.cus_id,
					customer_name: order.order.customer_name,
					customer_number: order.order.customer_number,
				};
			}
		});

		const siteArray = Object.entries(siteCounts).map(([site_id, data]) => ({
			site_id,
			count: data.count,
			site_name: data.site_name,
			site_number: data.site_number,
			site_total: data.site_total,
			customer_id: data.customer_id,
			customer_name: data.customer_name,
			customer_number: data.customer_number,
		}));

		const top_Sites = siteArray.sort((a, b) => b.count - a.count).slice(0, 10);
		const siteDataset = createDataset(top_Sites);
		return { siteDataset, siteArray };
	};

	const generateOrdersByPaymentModes = async (orders) => {
		const paymentModes = orders.reduce((acc, order) => {
			const paymentMode = order.order.order_payment_mode;
			if (paymentMode) {
				acc[paymentMode] = (acc[paymentMode] || 0) + 1;
			}
			return acc;
		}, {});

		const paymentModeArray = Object.entries(paymentModes).map(([paymentMode, count]) => ({
			name: paymentMode,
			value: count,
		}));

		const paymentModeDataset = createDataset(paymentModeArray);
		return { paymentModeDataset, paymentModeArray };
	};

	const generateOrderRevenueByPaymentMode = async (orders) => {
		const revenueByPaymentMode = {};

		orders.forEach((order) => {
			const paymentMode = order.order.order_payment_mode;
			const orderTotal = parseFloat(order.order.order_total) || 0;
			const orderDate = formatISODate(order.order.order_created_date);

			if (paymentMode) {
				if (!revenueByPaymentMode[paymentMode]) {
					revenueByPaymentMode[paymentMode] = {};
				}
				if (!revenueByPaymentMode[paymentMode][orderDate]) {
					revenueByPaymentMode[paymentMode][orderDate] = 0;
				}
				revenueByPaymentMode[paymentMode][orderDate] += orderTotal;
			}
		});

		const paymentModeRevenueArray = Object.entries(revenueByPaymentMode).map(([paymentMode, dates]) => {
			return Object.entries(dates).map(([date, value]) => ({
				date,
				name: paymentMode,
				value,
			}));
		});

		return paymentModeRevenueArray;
	};

	const renderSkeleton = () => {
		return <Skeleton variant="rounded" width={"100%"} height={"100%"} animation="wave" sx={{ opacity: "0.7" }} />;
	};

	const handleChartClick = (params, filterId, chartName) => {
		console.log("params from Charts", params);
		const existingFilterIndex = additionalFilters.findIndex((filter) => filter.id === filterId);
		if (existingFilterIndex !== -1) {
			const updatedFilters = [...additionalFilters];
			updatedFilters[existingFilterIndex].name = params.name;
			updatedFilters[existingFilterIndex].name_id = parseInt(params.data[0]) || null;
			setAdditionalFilters(updatedFilters);
		} else {
			setAdditionalFilters([
				...additionalFilters,
				{ id: filterId, name: params.name, chartName: chartName, name_id: parseInt(params.data[0]) || null },
			]);
		}
	};

	const handleOrderStatusChartClick = (params) => {
		handleChartClick(params, "order_status", t('UI_TEXT.ORDER_BY_STATUS'));
	};

	const handleOrderPaymentModeChartClick = (params) => {
		handleChartClick(params, "order_payment_mode", t('UI_TEXT.ORDER_BY_PAYMENT_MODE'));
	};

	const renderLoadingIcon = () => {
		return <ChartLoading />;
	};

	const handleShowTables = (metric) => {
		console.log("metric", metric);
		setSelectedMetric(metric);
		setShowCharts(false);
	};

	const clearChartFilter = (metric) => {
		console.log("metric", metric);
		setAdditionalFilters(additionalFilters.filter((filter) => filter.id !== metric));
	};

	const renderChartFilterIcons = (key) => {
		return (
			<Box
			data-tour="analytics-data-point"
				sx={{
					position: "absolute",
					top: 15,
					right: 20,
					display: "flex",
					flexDirection: "row",
					alignItems: "center",
					justifyContent: "center",
					gap: 1,
				}}>
				{additionalFilters.find((filter) => filter.id === key) && (
					<Box
						display="flex"
						flexDirection="row"
						alignItems="center"
						justifyContent="center"
						gap={1}
						cursor="pointer"
						sx={{
							backgroundColor: "primary.main",
							borderRadius: "15px",
							padding: "2px 5px 2px 15px",
							color: "#fff",
						}}>
						<Typography variant="body1" fontWeight={"bold"}>
							{additionalFilters.findIndex((filter) => filter.id === key) + 1}
						</Typography>
						<CloseIcon
							onClick={() => clearChartFilter(key)}
							sx={{ fontSize: "16px", cursor: "pointer", "&:hover": { color: "secondary.main" } }}
						/>
					</Box>
				)}
				<Tooltip title={t("UI_TEXT.TOOLTIP_SHOW_TABLE")}>
					<IconButton
						onClick={() => {
							handleShowTables({
								title: key,
								value: "54",
								change: "100%",
								changeType: "success",
								key: "total_orders",
							});
						}}>
						<TableChartIcon />
					</IconButton>
				</Tooltip>
			</Box>
		);
	};

	const hasChartData = (value) => {
		if (Array.isArray(value)) return value.length > 0;
		if (value && typeof value === "object") return Object.keys(value).length > 0;
		return false;
	};

	const renderChartContent = (key, content, dataValue, isLoading = chartLoading[key]) => {
		if (isLoading) return renderLoadingIcon();
		if (!hasOrders) return <NoDataText />;
		if (!hasChartData(dataValue)) return <NoDataText />;
		return content;
	};

	return (
		<Box className="chart-container" sx={{ px: 0 }}>
			<Box className="chart-row analytics-kpi-row" sx={{ height: "auto", minHeight: "125px" }}>
				{metrics.map((metric, index) => (
					<Box
						key={index}
						className="chart-card analytics-kpi-card"
						onClick={() => {
							console.log("metric", metric);
							handleShowTables(metric);
						}}>
						<Box>
							<Typography variant="h6">{metric.title}</Typography>
							<Box
								sx={{
									display: "flex",
									flexDirection: "row",
									columnGap: "15px",
									alignItems: "flex-start",
									justifyContent: "space-between",
									mt: 1,
								}}>
								{metricsLoading ? (
									<ChartLoading compact />
								) : (
									<Typography variant="h5" color="primary" sx={{ fontWeight: 600, lineHeight: 1.05 }}>
										{metric.value}
									</Typography>
								)}
							</Box>
						</Box>
						<Box>
							{metricsLoading ? (
								<ChartLoading compact />
							) : metric.changeType === "success" ? (
								<>
									<TrendingUpIcon color="success" sx={{ fontSize: "30px" }} />
									<Typography variant="body1" color="success">
										{metric.change}
									</Typography>
								</>
							) : metric.changeType === "error" ? (
								<>
									<TrendingDownIcon color="error" sx={{ fontSize: "30px" }} />
									<Typography variant="body1" color="error">
										{metric.change}
									</Typography>
								</>
							) : (
								<>
									<TrendingFlatIcon sx={{ fontSize: "30px", color: "text.secondary" }} />
									<Typography variant="body1" sx={{ color: "text.secondary" }}>
										{metric.change}
									</Typography>
								</>
							)}
						</Box>
					</Box>
				))}
			</Box>
			<Box className="chart-row">
				<Box className="chart-column chart-col-flex">
					<Typography variant="h5" fontWeight={"500"}>
						  {t('UI_TEXT.ORDERS_REVENUE_TITLE')}
					</Typography>
					<Box className="chart">
						{renderChartContent(
							"ordersRevenueCount",
							<CrossCharts
								data={chartData.ordersRevenueCount}
								xAxisLabel="date"
								yAxisLabels={[t('DASHBOARD.ORDERS')]}
								seriesConfig={[
									{ name: t('DASHBOARD.ORDERS'), type: "bar", dataKey: "orders" },
									// { name: t('UI_TEXT.REVENUE'), type: "line", dataKey: "revenue" },
								]}
							/>,
							chartData.ordersRevenueCount
						)}
					</Box>
				</Box>
				<Box className="chart-column chart-col-30" position="relative">
					{renderChartFilterIcons("order_status")}

					<Typography variant="h5" fontWeight={"500"}>
						{t('UI_TEXT.ORDERS_BY_STATUS')}
					</Typography>
					<Box className="chart">
						{renderChartContent(
							"orderStatuses",
							<DonutChart
								data={chartData.orderStatuses}
								onChartClick={handleOrderStatusChartClick}
								seriesName="Order Status"
								radius={["40%", "75%"]}
							/>,
							chartData.orderStatuses
						)}
					</Box>
				</Box>
			</Box>

			<Box className="chart-row">
				<Box className="chart-column chart-col-30" position="relative">
					<Typography variant="h5" fontWeight={"500"}>
						{t('UI_TEXT.TOP_PURCHASED_PARTS')}
					</Typography>
					<Box className="chart">
						{renderChartContent(
							"topParts",
							<HorizontalBarChart
								data={chartData.topParts}
								encode={{ x: "count", y: "product_name" }}
								sortConfig={{ dimension: "count", order: "asc" }}
							/>,
							chartData.topParts
						)}
					</Box>
				</Box>
				{/* <Box className="chart-column chart-col-flex">
					<Typography variant="h5" fontWeight={"500"}>
						Orders by Earnings
					</Typography>
					<Box className="chart">
						{loading ? renderLoadingIcon() : <GradientLineChart data={chartData.ordersRevenue} />}
					</Box>
				</Box> */}
				<Box className="chart-column chart-col-30" position="relative">
					<Typography variant="h5" fontWeight={"500"}>
						{t('UI_TEXT.TOP_CUSTOMER')}
					</Typography>
					<Box className="chart">
						{renderChartContent(
							"topCustomers",
							<HorizontalBarChart
								data={chartData.topCustomers}
								encode={{ x: "count", y: "customer_name" }}
								sortConfig={{ dimension: "count", order: "asc" }}
							/>,
							chartData.topCustomers
						)}
					</Box>
				</Box>
				<Box className="chart-column chart-col-30" position="relative">
					<Typography variant="h5" fontWeight={"500"}>
						{t('UI_TEXT.TOP_SITES')}
					</Typography>
					<Box className="chart">
						{renderChartContent(
							"topSites",
							<HorizontalBarChart
								data={chartData.topSites}
								encode={{ x: "count", y: "site_name" }}
								sortConfig={{ dimension: "count", order: "asc" }}
							/>,
							chartData.topSites
						)}
					</Box>
				</Box>
			</Box>
			<Box className="chart-row">
				{/* <Box className="chart-column chart-col-30" position="relative">
					{renderChartFilterIcons("order_payment_mode")}
					<Typography variant="h5" fontWeight={"500"}>
						{t('UI_TEXT.ORDERS_PAYMENT_MODES')}
					</Typography>
					<Box className="chart">
						{renderChartContent(
							"ordersByPaymentModes",
							<DonutChart
								data={chartData.ordersByPaymentModes}
								onChartClick={handleOrderPaymentModeChartClick}
								seriesName="Order Payment Mode"
								radius={["40%", "75%"]}
							/>,
							chartData.ordersByPaymentModes
						)}
					</Box>
				</Box> */}
				<Box className="chart-column chart-col-flex">
					<Typography variant="h5" fontWeight={"500"}>
						{t('UI_TEXT.ORDERS_REVENUE_BY_CUSTOMER')}
					</Typography>
					<Box className="chart">
						{renderChartContent(
							"ordersByCustomer",
							<SeriesBarChart data={chartData.ordersByCustomer} />,
							chartData.ordersByCustomer
						)}
					</Box>
				</Box>
				<Box className="chart-column chart-col-30" position="relative">
					{renderChartFilterIcons("order_payment_mode")}
					<Typography variant="h5" fontWeight={"500"}>
						{t('UI_TEXT.TOP_REQUESTED_PARTS')}
					</Typography>
					<Box className="chart">
						{renderChartContent(
							"topRequestedParts",
							<HorizontalBarChart
								data={requestedParts}
								encode={{ x: "count", y: "product_name" }}
								sortConfig={{ dimension: "count", order: "asc" }}
							/>,
							requestedParts,
							requestedPartsLoading
						)}
					</Box>
				</Box>
				{/* <Box className="chart-column chart-col-30">
					<Typography variant="h5" fontWeight={"500"}>
						Top Sites
					</Typography>
					<Box className="chart">{loading ? renderLoadingIcon() : <NoDataText />}</Box>
				</Box> */}
			</Box>

			<Box className="chart-row" sx={{ height: "600px !important" }}>
				{/* <Box className="chart-column chart-col-50">
					<Typography variant="h5" fontWeight={"500"}>
						Customer Orders
					</Typography>
					<Box className="chart">
						{loading ? renderLoadingIcon() : <TreeMap initialData={chartData.treeMapData} />}
					</Box>
				</Box> */}

				{/* <Box className="chart-column chart-col-flex">
					<Typography variant="h5" fontWeight={"500"}>
						{t('UI_TEXT.ORDERS_EARNINGS_PAYMENT')}
					</Typography>
					<Box className="chart">
						{renderChartContent(
							"ordersRevenueByPaymentMode",
							<StackedGradientLineChart data={chartData.ordersRevenueByPaymentMode} />,
							chartData.ordersRevenueByPaymentMode
						)}
					</Box>
				</Box> */}

				<Box className="chart-column chart-col-30">
					<Typography variant="h5" fontWeight={"500"}>
						{t('UI_TEXT.CUSTOMER_ORDER_PATTERS')}
					</Typography>
					<Box className="chart">
						{renderChartContent(
							"customerOrderPatterns",
							<SunBurst data={orders} />,
							orders
						)}
					</Box>
				</Box>
			</Box>
			{/* <Box className="chart-row">
				<Box className="chart-column chart-col-30">
					<Typography variant="h5" fontWeight={"500"}>
						Purchase vs. Request Ratio
					</Typography>
				</Box>
				<Box className="chart-column chart-col-30">
					<Typography variant="h5" fontWeight={"500"}>
						Top Spending Customer
					</Typography>
				</Box>
				<Box className="chart-column chart-col-30">
					<Typography variant="h5" fontWeight={"500"}>
						Most Requested (Non-Purchasable) Parts
					</Typography>
				</Box>
			</Box>
			<Box className="chart-row">
				<Box className="chart-column chart-col-50">
					<Typography variant="h5" fontWeight={"500"}>
						Repeat Orders
					</Typography>
				</Box>
				<Box className="chart-column chart-col-50">
					<Typography variant="h5" fontWeight={"500"}>
						Orders by Status
					</Typography>
				</Box>
			</Box> */}
		</Box>
	);
};

export default AnalyticsCharts;
